import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Merge Tailwind classes
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatMoney(value, options = {}) {
  const {
    currency = 'USD',
    showSign = false,
    compact = false,
  } = options;
  
  if (value === null || value === undefined) return '-';
  
  const absValue = Math.abs(value);
  const sign = value >= 0 ? '+' : '-';
  
  let formatted;
  
  if (compact) {
    if (absValue >= 1000000) {
      formatted = `$${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      formatted = `$${(absValue / 1000).toFixed(1)}K`;
    } else {
      formatted = `$${absValue.toFixed(2)}`;
    }
  } else {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absValue);
  }
  
  if (showSign && value !== 0) {
    return `${sign}${formatted.replace('$', '')}`;
  }
  
  return value < 0 ? `-${formatted}` : formatted;
}

// Format percentage
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined) return '-';
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

// Format price based on symbol
export function formatPrice(price, symbol = '') {
  if (price === null || price === undefined) return '-';
  
  const isForex = !['XAU', 'XAG', 'US30', 'US100', 'US500', 'GER40', 'UK100', 'JPN225'].some(
    s => symbol.includes(s)
  );
  
  if (isForex) {
    return price.toFixed(5);
  } else if (symbol.includes('XAU') || symbol.includes('XAG')) {
    return price.toFixed(2);
  } else {
    return price.toFixed(2);
  }
}

// Get trend color class
export function getTrendColor(trend) {
  switch (trend?.toLowerCase()) {
    case 'bullish':
    case 'up':
      return 'text-emerald-400';
    case 'bearish':
    case 'down':
      return 'text-red-400';
    default:
      return 'text-amber-400';
  }
}

// Get trend background class
export function getTrendBg(trend) {
  switch (trend?.toLowerCase()) {
    case 'bullish':
    case 'up':
      return 'bg-emerald-500/10 border-emerald-500/30';
    case 'bearish':
    case 'down':
      return 'bg-red-500/10 border-red-500/30';
    default:
      return 'bg-amber-500/10 border-amber-500/30';
  }
}

// Get score color class
export function getScoreColor(score) {
  if (score >= 75) return 'text-emerald-400';
  if (score >= 50) return 'text-amber-400';
  return 'text-red-400';
}

// Get score label
export function getScoreLabel(score) {
  if (score >= 75) return 'Strong Confluence';
  if (score >= 50) return 'Moderate Confluence';
  return 'Weak Confluence';
}

// Get status color
export function getStatusColor(status) {
  switch (status) {
    case 'pass':
      return 'text-emerald-400';
    case 'fail':
      return 'text-red-400';
    case 'warning':
      return 'text-amber-400';
    default:
      return 'text-slate-400';
  }
}

// Get status background
export function getStatusBg(status) {
  switch (status) {
    case 'pass':
      return 'bg-emerald-500/10';
    case 'fail':
      return 'bg-red-500/10';
    case 'warning':
      return 'bg-amber-500/10';
    default:
      return 'bg-slate-500/10';
  }
}

// Format date
export function formatDate(date, format = 'short') {
  if (!date) return '-';
  
  const d = new Date(date);
  
  if (format === 'short') {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  
  if (format === 'full') {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  
  if (format === 'time') {
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  
  if (format === 'datetime') {
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  
  return d.toISOString();
}

// Calculate Risk/Reward
export function calculateRR(entry, stopLoss, takeProfit, direction = 'long') {
  if (!entry || !stopLoss || !takeProfit) return null;
  
  const risk = Math.abs(entry - stopLoss);
  const reward = Math.abs(takeProfit - entry);
  
  if (risk === 0) return null;
  
  return (reward / risk).toFixed(2);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle function
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
